import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Pica from 'pica';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export interface CompressImageOptions {
  backgroundColor?: string;
  maxHeight?: number;
  maxWidth?: number;
}

@Injectable({
  providedIn: 'root'
})
export class ImageProcessingService {
  private readonly dataUrlPrefixRe = /^data\:([^\;]+)\;base64,/gmi;

  defaultMimeType = 'image/jpeg';

  constructor(private http: HttpClient) {

  }

  /** Returns an observable that emits the image when it is loaded. */
  ensureLoaded(img: HTMLImageElement) {
    if (img.complete) {
      return (img.naturalWidth !== 0) ? of(img) : throwError(new Error('Image did not load correctly'));
    }
    return new Observable<HTMLImageElement>(subscriber => {
      function loadHandler() {
        subscriber.next(img);
        subscriber.complete();
      }
      function errorHandler(err: ErrorEvent) {
        subscriber.error(err);
        subscriber.complete();
      }
      img.addEventListener('load', loadHandler);
      img.addEventListener('error', errorHandler);
      return () => {
        img.removeEventListener('load', loadHandler);
        img.removeEventListener('error', errorHandler);
      };
    });
  }


  /**
   * Resizes an image with pica, returning a canvas.  Use either canvas's toDataUrl, or toX methods in this library.
   */
  resizeImage(imgSrc: Blob | File | HTMLImageElement | string, opts: CompressImageOptions) {
    const pica = new Pica();
    opts = { ...{ mimeType: this.defaultMimeType }, ...opts };
    const img$ = imgSrc instanceof HTMLImageElement ? this.ensureLoaded(imgSrc) : this.toImage(imgSrc);
    return img$.pipe(
      switchMap((img) => {
        const scaleWidth = opts.maxWidth ? Math.min(opts.maxWidth / img.width, 1) : 1;
        const scaleHeight = opts.maxHeight ? Math.min(opts.maxHeight / img.height, 1) : 1;
        const scale = Math.min(scaleWidth, scaleHeight);
        const outputWidth = Math.round(img.width * scale);
        const outputHeight = Math.round(img.height * scale);

        const fromCanvas = this.makeImageCanvas(img, opts.backgroundColor);
        const toCanvas = document.createElement('canvas');
        toCanvas.width = outputWidth;
        toCanvas.height = outputHeight;

        const resizeOptions: Pica.PicaResizeOptions = {
          unsharpAmount: 25,
          unsharpRadius: .5,
          unsharpThreshold: 1
        };
        return from(pica.resize(fromCanvas, toCanvas, resizeOptions));
      })
    );
  }

  /**
   * Converts a blob or file to data url
   *
   * @param imgSrc The source of the image to be converted.
   * @param mimeType If the image has to be loaded to the canvas then it will be converted to an image of this mimeType
   */
  toDataUrl(imgSrc: Blob | File): Observable<string>
  toDataUrl(imgSrc: HTMLCanvasElement | HTMLImageElement | string, mimeType?: string, quality?: number): Observable<string>
  toDataUrl(imgSrc: Blob | File | HTMLCanvasElement | HTMLImageElement | string, mimeType?: string, quality?: number): Observable<string> {
    if (imgSrc instanceof Blob || imgSrc instanceof File) {
      return this.blobToDataUrl(imgSrc);
    }
    else if (imgSrc instanceof HTMLImageElement) {
      if (this.dataUrlPrefixRe.test(imgSrc.currentSrc)) { // return src if already data url.
        return of(imgSrc.currentSrc);
      }
      return this.imageToDataUrl(imgSrc, mimeType || this.defaultMimeType, quality || 1);
    }
    else if (imgSrc instanceof HTMLCanvasElement) {
      return of(imgSrc.toDataURL(mimeType ?? this.defaultMimeType, quality ?? 1));
    }
    else {
      if (this.dataUrlPrefixRe.test(imgSrc)) {
        return of(imgSrc);  // return if already data url.
      }
      return this.http.get(imgSrc, { responseType: 'blob' }).pipe(
        switchMap(x => this.blobToDataUrl(x)),
        // fallback to converting html image.
        catchError(() => this.toImage(imgSrc).pipe(
          switchMap(x => this.imageToDataUrl(x, mimeType || this.defaultMimeType, quality || 1)))
        )
      );
    }
  }

  /**
   * Converts a blob, file, or url to an image and emits the result when loaded.
   *
   * @param imgSrc Either a blob, File, or url.
   **/
  toImage(imgSrc: Blob | File | string) {
    return defer(() => {
      // defered because we don't want the loading to start once we set img.src;
      const img = new Image();
      if (typeof imgSrc === 'string') {
        const delim = imgSrc.includes('?') ? '&' : '?';
        const cacheBuster = (Math.random() * 999999999).toFixed(0);
        img.src = `${imgSrc}${delim}cachebuster=${cacheBuster}`;
      }
      else {
        img.src = URL.createObjectURL(imgSrc);
      }
      return this.ensureLoaded(img);
    });
  }

  blobToDataUrl(blob: Blob | File) {
    return new Observable<string>((subscriber) => {
      const fr = new FileReader();
      fr.addEventListener('load', loadHandler);
      fr.addEventListener('error', errorHandler);
      fr.addEventListener('abort', abortHandler);
      fr.readAsDataURL(blob);
      function abortHandler() {
        subscriber.complete();
      }
      function loadHandler() {
        subscriber.next(fr.result as string);
        subscriber.complete();
      }
      function errorHandler() {
        subscriber.error(fr.error);
        subscriber.complete();
      }
      return () => {
        fr.removeEventListener('load', loadHandler);
        fr.removeEventListener('error', errorHandler);
        fr.removeEventListener('abort', abortHandler);
        if (fr.readyState !== FileReader.DONE) {
          fr.abort();
        }
      };
    });
  }

  private imageToDataUrl(img: HTMLImageElement, mimeType?: string, quality?: number) {
    return this.ensureLoaded(img).pipe(
      map(x => this.makeImageCanvas(x).toDataURL(mimeType, quality))
    );
  }

  /** Makes a canvas with an image.  Useful as an intermidary for conversion. */
  private makeImageCanvas(img: HTMLImageElement, backgroundColor?: string) {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const context = canvas.getContext('2d', { alpha: true }) as CanvasRenderingContext2D;
    if (backgroundColor) {
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, canvas.width, canvas.height);
    }
    context.drawImage(img, 0, 0);
    return canvas;
  }
}
